import authService from '@/api/authService';
import tripApi from '@/api/tripApi';
import userApi from '@/api/userApi';
import { Desktop, Mobile, useIsDesktop } from '@/components/breakpoints';
import DuplicatesTripWidget from '@/components/duplicatesTripWidget';
import ExpensesPicker from '@/components/expensesPicker';
import FileUploader from '@/components/fileUploader';
import History from '@/components/history/history';
import NavigationBar from '@/components/navigationBar';
import NavigationWidget from '@/components/navigationWidget';
import { PageTitle } from '@/components/pageTitle';
import StateWidget from '@/components/stateWidget';
import { TripMenu } from '@/components/tripMenu';
import useLocalizeError from '@/hooks/useLocalizeError';
import { EditorContextProvider } from '@/lib/editorContext';
import { useHandleError } from '@/lib/errors';
import ExpensesListEntry from '@/pages/listView/components/expensesListEntry';
import TripEditorData from '@/pages/tripEditor/tripEditorData';
import { TripEditorForm } from '@/pages/tripEditor/tripEditorForm';
import TripEditorReceipts from '@/pages/tripEditor/tripEditorReceipts';
import TripReadonlyData from '@/pages/tripEditor/tripReadonlyData';
import { Box, Breadcrumbs, Button, Card, Container, Group, Modal, Skeleton } from '@mantine/core';
import { IconHome } from '@tabler/icons-react';
import { createFileRoute, Link, Outlet, useLocation } from '@tanstack/react-router';
import { hasClaim } from 'common/helpers/claimsHelpers';
import { deepEqual } from 'cross-state';
import { pick } from 'lodash';
import { useState } from 'react';
import { useTranslator } from 'translations';

export const Route = createFileRoute('/_auth/_home/t/$tripId')({
    component: RouteComponent,
    async loader({ params: { tripId } }) {
        return await tripApi.get(tripId).get();
    },
});

function RouteComponent() {
    const t = useTranslator();
    const location = useLocation();
    const { tripId } = Route.useParams();
    const [trip, error] = tripApi.get(tripId).useCache();
    const [owner] = userApi.get(trip?.ownerId ?? '').useCache({ disabled: !trip?.ownerId });
    const [appLogin] = authService.appLogin.useCache();
    const [expensesPickerOpen, setExpensesPickerOpen] = useState(false);
    const localizeError = useLocalizeError();
    const isDesktop = useIsDesktop();

    const canWrite =
        !!trip &&
        hasClaim(appLogin?.user, 'write', { forState: trip.state, forUser: trip.ownerId });
    const canDelete =
        !!trip &&
        hasClaim(appLogin?.user, 'delete', { forState: trip.state, forUser: trip.ownerId });

    useHandleError(error);

    return (
        <PageTitle key={tripId} title={t('tripEditor.header', { tripId })}>
            <TripEditorForm.Form
                original={trip}
                css={{
                    flex: 1,
                    display: 'flex',
                    flexDirection: 'column',
                }}
                localizeError={localizeError}
                initiallyTriggerValidations={location.state?.validate}
                autoSave={{
                    enabled: canWrite,
                    equals: (a, b) =>
                        deepEqual(
                            pick(a, ['tripData', 'expenses']),
                            pick(b, ['tripData', 'expenses']),
                            {
                                undefinedEqualsAbsent: true,
                            },
                        ),
                    async save(draft) {
                        await tripApi.update(tripId, draft.tripData, draft.expenses);
                    },
                    debounce: 5_000,
                    resetAfterSave: true,
                    validateBeforeSave: false,
                }}
            >
                <EditorContextProvider canWrite={canWrite} canDelete={canDelete}>
                    <NavigationBar
                        breadcrumbs={
                            owner && trip ? (
                                <Breadcrumbs>
                                    <Link to="/">
                                        <IconHome />
                                    </Link>

                                    <Link to="/" search={{ ownerId: [owner.id] }}>
                                        {owner.name}
                                    </Link>

                                    <Link to=".">{t('tripEditor.header', { tripId })}</Link>
                                </Breadcrumbs>
                            ) : (
                                <Skeleton w={300} />
                            )
                        }
                        additionalContent={
                            !trip ? null : (
                                <Box
                                    css={{
                                        display: 'flex',
                                        gap: 'var(--mantine-spacing-sm)',
                                    }}
                                >
                                    <NavigationWidget bucket={trip} />
                                    <TripMenu tripId={tripId} />
                                </Box>
                            )
                        }
                        justifyAdditionalContent="end"
                    />

                    <Box
                        css={{
                            flex: 1,
                            padding: 'var(--mantine-spacing-md)',
                            display: 'grid',
                            grid: `
                                    'state content' max-content
                                    'data content' max-content
                                    'history content' minmax(0, 1fr)
                                    / 450px minmax(0, 1fr)
                                `,
                            gap: 'var(--mantine-spacing-md)',

                            '@media (max-width: 900px)': {
                                grid: `
                                        'state'
                                        'content'
                                        'data'
                                        'history'
                                        / minmax(0, 1fr)
                                    `,
                            },
                        }}
                    >
                        <Card
                            withBorder
                            css={{
                                gridArea: 'state',
                                backgroundColor: 'var(--mantine-color-primary-filled)',
                                color: 'var(--mantine-color-white)',
                            }}
                        >
                            {trip ? <StateWidget bucket={trip} /> : <Skeleton w={300} />}
                        </Card>

                        <Card
                            withBorder
                            css={{
                                gridArea: 'history',
                                alignSelf: 'start',
                            }}
                        >
                            {trip ? <History bucket={trip} /> : <Skeleton h={400} />}
                        </Card>

                        <Card
                            withBorder
                            css={{
                                gridArea: 'data',
                            }}
                        >
                            {!trip ? (
                                <Skeleton h={400} />
                            ) : !canWrite ? (
                                <TripReadonlyData trip={trip} />
                            ) : (
                                <TripEditorData />
                            )}
                        </Card>

                        <Container
                            size="md"
                            css={{
                                gridArea: 'content',
                                width: '100%',
                                rowGap: isDesktop
                                    ? 'var(--mantine-spacing-xl)'
                                    : 'var(--mantine-spacing-sm)',
                                alignContent: 'start',
                            }}
                        >
                            <DuplicatesTripWidget bucket={trip} />

                            <TripEditorForm.FormState
                                selector={(form) => !!form.draft.expenses?.length}
                            >
                                {(hasExpenses) =>
                                    hasExpenses && (
                                        <>
                                            <Desktop>
                                                <ExpensesPicker />
                                            </Desktop>
                                            <Mobile>
                                                {trip && (
                                                    <ExpensesListEntry
                                                        trip={trip}
                                                        onClick={() => setExpensesPickerOpen(true)}
                                                        buttonPadding={canDelete}
                                                        css={{
                                                            padding:
                                                                'var(--mantine-spacing-xs) var(--mantine-spacing-sm)',
                                                        }}
                                                    />
                                                )}

                                                <Modal
                                                    opened={expensesPickerOpen}
                                                    onClose={() => setExpensesPickerOpen(false)}
                                                >
                                                    <ExpensesPicker />

                                                    <Group mt="md" justify="flex-end">
                                                        <Button
                                                            onClick={() =>
                                                                setExpensesPickerOpen(false)
                                                            }
                                                        >
                                                            {t('ok')}
                                                        </Button>
                                                    </Group>
                                                </Modal>
                                            </Mobile>
                                        </>
                                    )
                                }
                            </TripEditorForm.FormState>

                            {trip ? <TripEditorReceipts trip={trip} /> : <Skeleton h="100%" />}
                        </Container>
                    </Box>
                </EditorContextProvider>
            </TripEditorForm.Form>

            <Outlet />
            <FileUploader active={canWrite} tripId={tripId} />
        </PageTitle>
    );
}
